import React, { useCallback, useContext, useEffect, useState, useMemo } from "react";
import { Filter, ArrowUpDown, Layers } from "lucide-react";
import BryntumContext from "../../context/bryntumContext.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { canSaveView } from "../../../other/utils/utils.jsx";
import { getFilters, getSorters, getGroupers, getFilterValue, getColumnNameByField } from "@/other/utils/viewStateUtils.jsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip.tsx";
import { STATE_ID_TO_VIEW_NAME } from "../../../other/feature/views/widget/ViewStateGridMapping.jsx";
import { Button } from "@/modern/components/ui/button.tsx";

const ViewStateBadges = () => {
  const { editorHandler, viewManager } = useContext(BryntumContext);
  const [pageViewsRef, setPageViewsRef] = useState(null);
  const [viewStates, setViewStates] = useState({ filters: 0, sorters: 0, groupers: 0 });
  const [detailedStates, setDetailedStates] = useState({ filters: [], sorters: [], groupers: [] });

  const hasStates = viewStates.filters > 0 || viewStates.sorters > 0 || viewStates.groupers > 0;

  const handleBadgeClick = () => {
    if (canSaveView(viewManager.renderState.selectedView?.viewType)) {
      editorHandler.openEditor(EDITOR_KEYS.viewInspectorEditor, null, { viewRecord: viewManager.renderState.selectedView }, { by: "recordType", useExisting: true });
    }
  };

  const calculateCombinedViewStates = useCallback(() => {
    if (!pageViewsRef || !viewManager?.renderState?.selectedView?.viewType) return;

    let totalFilters = 0;
    let totalSorters = 0;
    let totalGroupers = 0;
    const allFilters = [];
    const allSorters = [];
    const allGroupers = [];

    Object.entries(pageViewsRef).forEach(([viewType, gridViewRef]) => {
      if (gridViewRef.isDestroyed || !gridViewRef.store) return;

      const selectedViewType = viewManager.renderState.selectedView.viewType;
      const viewConfig = STATE_ID_TO_VIEW_NAME[`${selectedViewType}-${viewType}`];

      if (viewConfig) {
        const columns = viewConfig.config.columns.reduce((acc, col) => acc.concat(col.children || col), []);

        // Extract filters with details
        const filters = getFilters(
          gridViewRef.store.filters.values.map((item) => ({
            value: item.value,
            property: item.property,
            hidden: item.hidden,
            operator: item.operator,
            type: item.type,
            caseSensitive: item.caseSensitive
          })) || []
        );

        filters.forEach((filter) => {
          const columnName = getColumnNameByField(columns, filter.property);
          const filterValue = getFilterValue(filter);
          allFilters.push({
            viewName: viewConfig.title,
            columnName: columnName || filter.property || "Unknown",
            value: filterValue,
            caseSensitive: filter.type === "string" && filter.value ? filter.caseSensitive : false,
            disabled: filter.disabled
          });
        });

        // Extract sorters with details
        const sorters = getSorters(gridViewRef.store.sorters || []);
        sorters.forEach((sorter) => {
          const columnName = getColumnNameByField(columns, sorter.field);
          allSorters.push({
            viewName: viewConfig.title,
            columnName: columnName || sorter.field || "Unknown",
            direction: sorter.ascending ? "Ascending" : "Descending"
          });
        });

        // Extract groupers with details
        const groupers = getGroupers(gridViewRef.store.groupers || []);
        groupers.forEach((grouper) => {
          const columnName = getColumnNameByField(columns, grouper.field);
          allGroupers.push({
            viewName: viewConfig.title,
            columnName: columnName || grouper.field || "Unknown",
            type: grouper.custom ? "Settings" : grouper.ascending ? "Ascending" : "Descending"
          });
        });

        totalFilters += filters.length;
        totalSorters += sorters.length;
        totalGroupers += groupers.length;
      }
    });

    setViewStates({
      filters: totalFilters,
      sorters: totalSorters,
      groupers: totalGroupers
    });

    setDetailedStates({
      filters: allFilters,
      sorters: allSorters,
      groupers: allGroupers
    });
  }, [pageViewsRef, viewManager]);

  useEffect(() => {
    if (!viewManager) return;

    const detacher = viewManager.addListenerForPageViewsRef((value) => {
      setPageViewsRef(value);
    });

    return detacher;
  }, [viewManager]);

  useEffect(() => {
    if (!pageViewsRef) return;

    calculateCombinedViewStates();

    const detachers = Object.values(pageViewsRef).map((gridViewRef) => {
      return gridViewRef.store.on({
        group: calculateCombinedViewStates,
        sort: calculateCombinedViewStates,
        filter: calculateCombinedViewStates
      });
    });

    return () => {
      detachers.forEach((detacher) => detacher());
    };
  }, [pageViewsRef, calculateCombinedViewStates]);

  // Helper function to group items by viewName
  const groupByViewName = (items) => {
    return items.reduce((acc, item) => {
      if (!acc[item.viewName]) {
        acc[item.viewName] = [];
      }
      acc[item.viewName].push(item);
      return acc;
    }, {});
  };

  // Unified tooltip content renderer
  const renderTooltipContent = (items, config) => {
    if (items.length === 0) return null;

    const groupedItems = groupByViewName(items);
    const { icon: Icon, title } = config;

    return (
      <div className="min-w-72 max-w-96">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-muted-foreground/20">
          <div className="flex items-center justify-center w-5 h-5 rounded-sm">
            <Icon className="h-4 w-4 text-foreground" />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">{title}</div>
          </div>
        </div>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {Object.entries(groupedItems).map(([viewName, groupItems]) => (
            <div key={viewName} className="space-y-1">
              <div className="text-xs text-muted-foreground font-medium px-2 py-1">{viewName}</div>
              <div className="space-y-0.5">
                {groupItems.map((item, idx) => (
                  <div key={idx} className="px-2 py-1.5 rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-default">
                    {config.renderItem(item)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Memoized tooltip content for better performance
  const filterTooltipContent = useMemo(() => {
    return renderTooltipContent(detailedStates.filters, {
      icon: Filter,
      title: "Filters",
      singularLabel: "filter",
      pluralLabel: "filters",
      renderItem: (filter) => (
        <div className="text-xs text-foreground">
          <span>{filter.columnName}:</span> <span>Filter on</span> <span className="text-foreground">"{filter.value}"</span>
          {filter.caseSensitive && <span className="ml-1.5 text-xs text-muted-foreground italic opacity-80">(case sensitive)</span>}
        </div>
      )
    });
  }, [detailedStates.filters]);

  const sorterTooltipContent = useMemo(() => {
    return renderTooltipContent(detailedStates.sorters, {
      icon: ArrowUpDown,
      title: "Sort",
      singularLabel: "sorter",
      pluralLabel: "sorters",
      renderItem: (sorter) => (
        <div className="text-xs text-foreground">
          <span>{sorter.columnName}:</span> <span>Sort</span>{" "}
          <span className="text-foreground">
            {sorter.direction === "Ascending" ? "↑" : "↓"} {sorter.direction}
          </span>
        </div>
      )
    });
  }, [detailedStates.sorters]);

  const grouperTooltipContent = useMemo(() => {
    return renderTooltipContent(detailedStates.groupers, {
      icon: Layers,
      title: "Groups",
      singularLabel: "grouper",
      pluralLabel: "groupers",
      renderItem: (grouper) => (
        <div className="text-xs text-foreground">
          <span>{grouper.columnName}:</span> <span>Group by</span> <span className="text-foreground">{grouper.type === "Settings" ? "Settings" : grouper.type}</span>
        </div>
      )
    });
  }, [detailedStates.groupers]);

  // Unified badge renderer
  const renderBadge = (count, icon, tooltipContent) => {
    const Icon = icon;
    return (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <Button variant="outline" size="sm" onClick={handleBadgeClick}>
            <Icon />
            <span>{count}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="p-2.5 shadow-md border">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    );
  };

  if (!hasStates) return null;

  return (
    <div className="flex items-center gap-2">
      {viewStates.filters > 0 && renderBadge(viewStates.filters, Filter, filterTooltipContent)}
      {viewStates.sorters > 0 && renderBadge(viewStates.sorters, ArrowUpDown, sorterTooltipContent)}
      {viewStates.groupers > 0 && renderBadge(viewStates.groupers, Layers, grouperTooltipContent)}
    </div>
  );
};

export default ViewStateBadges;